import React, { useEffect, useState } from "react";
import Button from "../../ui/Button";
import ModalInput from "../../ui/ModalTextInput";
import CustomModalContainer from "../../../components/modals/CustomModalContainer";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import {
  fetchAdminProfileAsync,
  updateAdminProfileAsync,
} from "../../../redux/features/adminSlice";
import * as Yup from "yup";

interface EditProfileModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type formType = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
};

const nameRegex = /^[a-zA-ZÀ-ÖØ-öø-ÿ' -]{2,}$/;
const phoneRegex = /^\+?[0-9\s\-().]{7,20}$/;

const editProfileSchema = Yup.object().shape({
  firstName: Yup.string()
    .matches(nameRegex, "First name must be valid")
    .required("First name is required"),
  lastName: Yup.string()
    .matches(nameRegex, "Last name must be valid")
    .required("Last name is required"),
  email: Yup.string()
    .email("Enter a valid email address")
    .required("Email is required"),
  phoneNumber: Yup.string()
    .matches(phoneRegex, "Enter a valid phone number")
    .required("Phone number is required"),
});

const EditProfileModal: React.FC<EditProfileModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { adminProfile, isLoading } = useAppSelector((state) => state.admin);
  const [formData, setFormData] = useState<formType>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
  });
  const [errors, setErrors] = useState<Partial<formType>>({});
  const dispatch = useAppDispatch();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: undefined }); // clear error on change
  };

  const handleUpdate = async () => {
    try {
      await editProfileSchema.validate(formData, { abortEarly: false });

      await dispatch(updateAdminProfileAsync(formData)).unwrap();
      onSuccess();
      dispatch(fetchAdminProfileAsync());
      handleDiscard();
    } catch (err: any) {
      if (err.name === "ValidationError") {
        const formErrors: Partial<formType> = {};
        err.inner.forEach((validationErr: any) => {
          formErrors[validationErr.path as keyof formType] =
            validationErr.message;
        });
        setErrors(formErrors);
      } else {
        console.log(err);
      }
    }
  };

  const handleDiscard = () => {
    if (adminProfile) {
      setFormData({
        firstName: adminProfile.firstName || "",
        lastName: adminProfile.lastName || "",
        email: adminProfile.email || "",
        phoneNumber: adminProfile.phoneNumber || "",
      });
    }
    onClose();
  };

  useEffect(() => {
    if (adminProfile) {
      setFormData({
        firstName: adminProfile.firstName || "",
        lastName: adminProfile.lastName || "",
        email: adminProfile.email || "",
        phoneNumber: adminProfile.phoneNumber || "",
      });
    }
  }, [adminProfile]);

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Edit Profile"
      footer={
        <div className="flex justify-between relative bottom-0">
          <Button
            type="button"
            onClick={handleDiscard}
            label="Discard"
            layout="inline" variant="ghost"
          />
          <Button
            label="Update profile"
            type="submit"
            layout="inline"
            onClick={handleUpdate}
            disabled={
              !formData.firstName ||
              !formData.lastName ||
              !formData.email ||
              !formData.phoneNumber
            }
            isLoading={isLoading}
          />
        </div>
      }
      open={open}
      onClose={handleDiscard}
    >
      <div className="p-6">
        <form>
          <ModalInput
            label="First name"
            placeholder="Enter first name"
            required
            name="firstName"
            onChange={handleInputChange}
            value={formData.firstName}
            error={errors.firstName}
          />
          <ModalInput
            label="Last name"
            type="text"
            placeholder="Enter last name"
            required
            name="lastName"
            onChange={handleInputChange}
            value={formData.lastName}
            error={errors.lastName}
          />
          <ModalInput
            label="Email address"
            type="email"
            placeholder="Enter email address"
            required
            name="email"
            value={formData.email}
            error={errors.email}
          />
          <ModalInput
            label="Phone number"
            type="tel"
            placeholder="Enter phone number"
            required
            name="phoneNumber"
            onChange={handleInputChange}
            value={formData.phoneNumber}
            error={errors.phoneNumber}
          />
        </form>
      </div>
    </CustomModalContainer>
  );
};

export default EditProfileModal;
